import { useState, useEffect } from 'react';
import { ContactViewAudit } from '../../types';
import { supabase } from '../../lib/supabase';
import { Loading } from '../../components/ui/Loading';
import { Empty } from '../../components/ui/Empty';
import { ErrorAlert } from '../../components/ui/ErrorAlert';
import { displayPhone } from '../../utils/phone';

export const AdminAuditLogs = () => {
  const [logs, setLogs] = useState<ContactViewAudit[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userFilter, setUserFilter] = useState('');
  const [contactFilter, setContactFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const loadLogs = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: dbError } = await supabase
        .from('contact_view_audit')
        .select('*')
        .order('viewed_at', { ascending: false })
        .limit(1000);
      if (dbError) throw dbError;
      setLogs((data ?? []) as ContactViewAudit[]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLogs();
  }, []);

  const filtered = logs.filter((log) => {
    const userMatch = !userFilter || (log.viewer_name ?? '').toLowerCase().includes(userFilter.toLowerCase());
    const contactMatch = !contactFilter || (log.contact_name ?? '').toLowerCase().includes(contactFilter.toLowerCase());
    const viewed = new Date(log.viewed_at);
    const fromMatch = !fromDate || viewed >= new Date(fromDate);
    const toMatch = !toDate || viewed <= new Date(`${toDate}T23:59:59`);
    return userMatch && contactMatch && fromMatch && toMatch;
  });

  if (loading) return <Loading full />;
  if (error) return <ErrorAlert message={error} onRetry={loadLogs} />;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Contact View Audit</h1>
        <p className="text-sm text-gray-600">Track which users viewed which contacts. Only visible to Admins.</p>
      </div>
      <div className="grid grid-cols-1 gap-3 rounded-lg border border-gray-200 bg-white p-4 sm:grid-cols-2 lg:grid-cols-4">
        <input
          type="text"
          placeholder="Filter by user name"
          value={userFilter}
          onChange={(e) => setUserFilter(e.target.value)}
          className="rounded border border-gray-300 px-3 py-2 text-sm"
        />
        <input
          type="text"
          placeholder="Filter by contact name"
          value={contactFilter}
          onChange={(e) => setContactFilter(e.target.value)}
          className="rounded border border-gray-300 px-3 py-2 text-sm"
        />
        <input
          type="date"
          placeholder="From"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          className="rounded border border-gray-300 px-3 py-2 text-sm"
        />
        <input
          type="date"
          placeholder="To"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          className="rounded border border-gray-300 px-3 py-2 text-sm"
        />
      </div>
      {filtered.length === 0 ? (
        <Empty title="No audit records" message="No contact views match the selected filters." />
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-sm">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">User</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">User Mobile</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Contact</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Contact Mobile</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Viewed At</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filtered.map((log) => (
                <tr key={log.id}>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{log.viewer_name ?? '-'}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{log.viewer_mobile ? displayPhone(log.viewer_mobile) : '-'}</td>
                  <td className="px-4 py-3 text-sm text-gray-900">{log.contact_name ?? '-'}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{log.contact_mobile ? displayPhone(log.contact_mobile) : '-'}</td>
                  <td className="px-4 py-3 text-sm text-gray-500">{new Date(log.viewed_at).toLocaleString('en-IN')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
